import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    IProjectReleaseLotMigrationSourceSelectionGroupDto,
    IProjectReleaseLotMigrationSourceSelectionLotDto,
    IProjectReleaseLotMigrationSourceSelectionRowDto,
    LOT_JOB_STATUS_ENUM,
    LOT_TYPE_ENUM,
    LotTypeEnumId
} from '@classictechsolutions/hubapi-transpiled-enums';
import { CbTableModule } from 'cb-hub-lib';

@Component({
    selector: 'cb-lot-migrator-source-selection',
    standalone: true,
    imports: [
        MatCheckboxModule,
        MatExpansionModule,
        MatIconModule,
        MatTooltipModule,
        CbTableModule
    ],
    templateUrl: './lot-migrator-source-selection.component.html',
    styleUrls: ['./lot-migrator-source-selection.component.scss']
})
export class LotMigratorSourceSelectionComponent {
    public static readonly ELIGIBLE_GROUP_KEY = 'eligible';
    public static readonly INELIGIBLE_GROUP_KEY = 'ineligible';

    @Input() public groups: IProjectReleaseLotMigrationSourceSelectionGroupDto[] = [];
    @Input() public maxLotsToMigrate = 0;
    @Output() public readonly selectionChange = new EventEmitter<number[]>();
    @Output() public readonly lotTypeChange = new EventEmitter<LotTypeEnumId>();

    public readonly ELIGIBLE_GROUP_KEY = LotMigratorSourceSelectionComponent.ELIGIBLE_GROUP_KEY;
    public readonly INELIGIBLE_GROUP_KEY = LotMigratorSourceSelectionComponent.INELIGIBLE_GROUP_KEY;
    public readonly LOT_TYPE_ENUM = LOT_TYPE_ENUM;
    public readonly LOT_JOB_STATUS_ENUM = LOT_JOB_STATUS_ENUM;
    public selectedLotIds = new Set<number>();
    public selectedLotType: LotTypeEnumId = null;
    public eligibleExpanded = true;
    public ineligibleExpanded = false;

    public setGroupExpanded(
        group: IProjectReleaseLotMigrationSourceSelectionGroupDto,
        expanded: boolean
    ): void {
        if (group.key === this.ELIGIBLE_GROUP_KEY) {
            this.eligibleExpanded = expanded;
            return;
        }

        this.ineligibleExpanded = expanded;
    }

    public isGroupExpanded(group: IProjectReleaseLotMigrationSourceSelectionGroupDto): boolean {
        return group.key === this.ELIGIBLE_GROUP_KEY
            ? this.eligibleExpanded
            : this.ineligibleExpanded;
    }

    public getDisplayReasons(lot: IProjectReleaseLotMigrationSourceSelectionLotDto): string[] {
        if (lot.lotType !== LotTypeEnumId.Unit) {
            return lot.migrationEligibilityReasons;
        }

        return lot.migrationEligibilityReasons.filter(
            reason =>
                reason !== 'Child Units can only be selected by selecting their parent block.' &&
                reason !==
                    'The Migrate Lots Wizard is only available for Standalone or Terraced Block Lot Types.'
        );
    }

    public isSelected(lot: IProjectReleaseLotMigrationSourceSelectionLotDto): boolean {
        return this.selectedLotIds.has(lot.id);
    }

    public canSelect(row: IProjectReleaseLotMigrationSourceSelectionRowDto): boolean {
        return this.getDisabledReason(row) == null;
    }

    public getDisabledReason(row: IProjectReleaseLotMigrationSourceSelectionRowDto): string | null {
        const lot = row.lot;
        const childLots = row.childLots;

        if (lot.canBeMigrated === false) {
            return '';
        }

        if (this.selectedLotType != null && this.selectedLotType !== lot.lotType) {
            return 'The Migrate Lots Wizard requires all selected Lots to be the same Lot Type';
        }

        if (!this.isSelected(lot)) {
            const projectedCount = this.selectedLotIds.size + 1 + childLots.length;
            if (this.maxLotsToMigrate >= 0 && projectedCount > this.maxLotsToMigrate) {
                return `You can only select up to ${this.maxLotsToMigrate} lot(s) for this migration.`;
            }
        }

        return null;
    }

    public handleChecked(
        checked: boolean,
        row: IProjectReleaseLotMigrationSourceSelectionRowDto
    ): void {
        const lot = row.lot;
        const childLots = row.childLots;

        if (checked) {
            this.selectedLotIds.add(lot.id);
            childLots.forEach(childLot => this.selectedLotIds.add(childLot.id));
            if (this.selectedLotType == null) {
                this.selectedLotType = lot.lotType;
            }
        } else {
            this.selectedLotIds.delete(lot.id);
            childLots.forEach(childLot => this.selectedLotIds.delete(childLot.id));
            if (this.selectedLotIds.size < 1) {
                this.selectedLotType = null;
            }
        }

        this.emitSelection();
    }

    private emitSelection(): void {
        this.selectionChange.emit(Array.from(this.selectedLotIds));
        this.lotTypeChange.emit(this.selectedLotType);
    }
}
